import type { ContentTypes } from '@lumapps/content-types/types';
import type { Group } from '@lumapps/groups/types';
import type { PickerMetaData } from '@lumapps/metadata-pickers/types';
import type { TranslateObject } from '@lumapps/translations';
import type {
    UserDirectoryComponentProperties,
    UserDirectoryTemplateComponentType,
} from '@lumapps/user-directory/types';
import type { User } from '@lumapps/user/types';

import type { IconValue } from './utils/icon';

/**
 * A value held by a group picker field: a full `Group` for anything the user just picked, or a raw
 * id string for a value loaded from the backend. Normalise with `utils/groups#toGroupIds` before saving.
 */
export type GroupSelection = Group | string;

/**
 * Component choices the backend expresses as a `type` + `secondaryType` pair rather than as a single
 * component type, collapsed into one option so the form can offer them alongside the plain types.
 *
 * `user` is the user picker: fully supported by the backend, but stored as `inputSelect` +
 * `properties.secondaryType: 'userPicker'`. `TEMPLATE_COMPONENT_TO_TYPE` maps it on the way out and
 * `userDirectoryToFormValues` maps it back on the way in, so this shorthand stays inside form state.
 */
export enum TemplateFieldComponentAlias {
    user = 'user',
}

/** Backend-aligned component type values, plus the UI-only aliases. */
export type TemplateFieldComponent = UserDirectoryTemplateComponentType | TemplateFieldComponentAlias;

export enum ValidationPatternMode {
    None = 'none',
    Preset = 'preset',
    Custom = 'custom',
}

export interface FieldOption {
    /**
     * The catalogue name of the default field (`API_PROFILE_FIELD_*`), or `API_PROFILE_FIELD_CUSTOM`
     * for a user-typed pattern. Persisted as `properties.boundMap.name`.
     */
    value: string;
    label: string;
    directoryPattern?: string;
    /** Input component this default field maps to. Absent on the custom option. */
    component?: TemplateFieldComponent;
}

export interface ComponentOption {
    value: TemplateFieldComponent;
    label: string;
}

export interface UserDirectoryTemplateFieldFormValues {
    title: Record<string, string>;
    /** Either the picker's `Icon` object or a bare name; normalise with `utils/icon#toIconName`. */
    icon?: IconValue;
    isDirectoryField?: boolean;
    field?: FieldOption;
    customPattern?: string;
    component?: ComponentOption;
    /** Persisted as `properties.displayInList`. */
    shownInDirectory?: boolean;
    /** Persisted as `properties.displayInFilter`. */
    usableToFilterUsers?: boolean;
    visibleBy?: GroupSelection[];
    /** Persisted as `properties.fullWidth`. */
    showInFullWidth?: boolean;
    editableByUser?: boolean;
    /** UI-only: gates `editFeeds`, which is what the backend actually stores. */
    editableByOtherUsers?: boolean;
    // Text-specific
    isTextArea?: boolean;
    /**
     * UI-only: whether the field validates its value at all. Derived on load from whether a
     * `validationRegex` is stored, and gates what `resolveValidationRegex` persists — it is never
     * sent to the backend, which only knows `validationRegex`.
     */
    hasValidationPattern?: boolean;
    validationPatternMode?: ValidationPatternMode;
    validationPattern?: string;
    errorMessage?: Record<string, string>;
    // Metadata-specific: the picker accepts a resolved family or a bare id, and resolves the name itself.
    metadata?: PickerMetaData | string | null;
    // Directory binding
    isBound?: boolean;
    // Access control
    editFeeds?: GroupSelection[];
}

export interface UserDirectoryTemplateField extends UserDirectoryTemplateFieldFormValues {
    uuid: string;
    /** Backend type — set when saving; consumed by the API. 'widget' is a backend-injected placeholder type. */
    type?: UserDirectoryTemplateComponentType | 'widget';
    /** Backend properties — set when saving; consumed by the API. */
    properties?: UserDirectoryComponentProperties;
}

export interface UserDirectoryFormValues {
    title: Record<string, string>;
    slug: Record<string, string>;
    feedKeys: GroupSelection[];
    isDefaultUserDirectory?: boolean;
    userGroups: GroupSelection[];
    template: { components: UserDirectoryTemplateField[] };
    displayUserProfile?: boolean;
    displayFilters?: boolean;
    allowProfileUpdate?: boolean;
    displayOrganizationChart?: boolean;
    helpTitle?: Record<string, string>;
    helpContent?: Record<string, string>;
    isDeletableByUser?: boolean;
}

export interface UserDirectoryListItem {
    id: string;
    type: typeof ContentTypes.USER_DIRECTORY;
    title: Record<string, string>;
    slug?: TranslateObject<string>;
    updatedAt?: string;
    createdAt?: string;
    authorDetails?: User;
    updatedByDetails?: User;
    feedKeys?: string[];
    properties?: {
        userGroups?: string[];
        allowProfileUpdate?: boolean;
        displayOrganizationChart?: boolean;
    };
    template?: {
        components?: Array<{
            uuid: string;
            title: Record<string, string>;
            properties?: { icon?: string };
        }>;
    };
}
